/// <amd-module name="Feed/Core/Redux/Navigation/NavigationSagas" />
import {
    HubActionStrings,
    SupportedAnonymousHubActionStrings,
    SupportedHubActionStrings
} from "Feed/Common/Constants/Constants";
import * as Resources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { Action, ActionsOfType, ActionWithPayload } from "redux/Utils/ActionHelper";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssUserClaimsService, UserClaims } from "VSS/Platform/UserClaims";
import { FeedHubActions } from "../FeedHub/FeedHubActions";
import { MetaTypes, NavigationActions, NavigationActionTypes, replaceHistoryPoint } from "./NavigationActions";
import { IHubState } from "./NavigationContracts";

export default function* rootSaga(): SagaIterator {
    yield effects.takeLatest(NavigationActionTypes.NavigateToHub, navigateToHub);
    yield effects.takeLatest(NavigationActionTypes.NavigateToPage, navigateToPage);
    yield effects.takeEvery((action: Action<any>) => action.meta && action.meta[MetaTypes.FEED_HUB_ACTION], hubSwitch);

    yield effects.call(loadInitialNavigationData);
}

export function* loadInitialNavigationData(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");

    let navState: { [key: string]: string } = historyService.getState();
    navState._a = navState._a || HubActionStrings.ViewFeed;
    const initialHubState = toHubState(navState);

    yield effects.put(replaceHistoryPoint(NavigationActions.navigateToPage(initialHubState)));
}

// if an action has FEED_HUB_ACTION metadata, queue an action to switch to that hub - for legacy
export function* hubSwitch(action: ActionWithPayload<any, any>): SagaIterator {
    const newHub = action.meta![MetaTypes.FEED_HUB_ACTION];

    yield effects.put(FeedHubActions.clearErrorMessage());
    yield effects.put(NavigationActions.navigateToHub(newHub));
}

// used by legacy - doesn't update history, just ensures action is updated in UI
function* navigateToHub(action: ActionWithPayload<NavigationActionTypes.NavigateToHub, string>): SagaIterator {
    const newState: IHubState = {
        action: action.payload
    };

    yield effects.put(NavigationActions.hubStateChange(newState));
}

function* navigateToPage(action: ActionsOfType<NavigationActions, NavigationActionTypes.NavigateToPage>): SagaIterator {
    const hubState = action.payload;

    // check that the hubState action is valid
    try {
        yield* validateHubAction(hubState.action);
    } catch (error) {
        yield effects.put(FeedHubActions.setErrorMessage(error));
        hubState.action = HubActionStrings.ViewFeed;
    }

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    const currentState: { [key: string]: string } = historyService.getState();

    const navState = toNavState(currentState, hubState);
    const newHubState = toHubState(navState);

    if (action.meta && action.meta![MetaTypes.REPLACE_HISTORY] === true) {
        historyService.replaceState(navState, undefined, undefined, newHubState);
    } else {
        historyService.pushState(navState, undefined, undefined, newHubState);
    }

    yield effects.put(NavigationActions.hubStateChange(newHubState));
}

// Validate that the hubAction exists, and the user has permission to use it
export function* validateHubAction(action: string): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const userClaimService = pageContext.getService<IVssUserClaimsService>("IVssUserClaimsService");
    const hasMemberClaim: boolean = userClaimService.hasClaim(UserClaims.Member);

    const supportedHubActions = hasMemberClaim ? SupportedHubActionStrings : SupportedAnonymousHubActionStrings;

    // if the current user doesn't have permission to the selected action,
    // or the action doesn't exist, throw an error
    if (supportedHubActions.has(action) === false) {
        const error = {
            message: Utils_String.format(Resources.Error_UrlActionNotSupported, action)
        } as IError;

        throw error;
    }
}

function toHubState(navState: { [key: string]: string }): IHubState {
    return {
        action: navState._a,
        feed: navState.feed,
        package: navState.package,
        version: navState.version,
        upstreamSource: navState.upstreamSource,
        protocolType: navState.protocolType,
        preferRelease: navState.preferRelease,
        view: navState.view
    } as Required<IHubState>;
}

function toNavState(currentState: { [key: string]: string }, hubState: IHubState): { [key: string]: string } {
    let navState = { ...currentState };
    navState._a = hubState.action;

    const blankHubState: Required<IHubState> = {
        action: "",
        feed: "",
        package: "",
        version: "",
        upstreamSource: "",
        protocolType: "",
        preferRelease: "",
        view: ""
    };

    // Iterate through all possible IHubState properties
    Object.keys(blankHubState)
        .filter(value => {
            // currentState.action is defined elsewhere, don't override it
            return value != "action";
        })
        .forEach(value => {
            if (hubState[value] == undefined) {
                // remove the properties that exist in currentState, but not hubState
                delete navState[value];
            } else {
                // add the properties that are defined in hubState
                navState[value] = hubState[value];
            }
        });

    delete navState.showFilters;

    return navState;
}
